import { Bed, Bath, Users, Star } from 'lucide-react';
import { Property } from '@/types/property';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Link from 'next/link';
import Image from 'next/image';

interface PropertyCardProps {
    property: Property;
    isHovered: boolean;
    onHover: () => void;
    onLeave: () => void;
}

export default function PropertyCard({ property, isHovered, onHover, onLeave }: PropertyCardProps) {
    const propertyImages = property?.images || property.media?.gallery || [];
    return (
        <div
            className={`bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer ${isHovered ? 'ring-2 ring-secondary' : ''
                }`}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
        >
            <div className="relative h-56 overflow-hidden">
                <Carousel className="w-full h-full">
                    <CarouselContent>
                        {propertyImages?.map((imageUrl: any | { url: string }, index: number) => (
                            <CarouselItem key={index}>
                                <Image
                                    height={240}
                                    width={320}
                                    src={imageUrl?.url || imageUrl}
                                    alt={`${property.title} - Image ${index + 1}`}
                                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    {(propertyImages?.length || 0) > 1 && (
                        <>
                            <CarouselPrevious className="left-2 bg-white/80 hover:bg-white shadow-lg" />
                            <CarouselNext className="right-2 bg-white/80 hover:bg-white shadow-lg" />
                        </>
                    )}
                </Carousel>
                {/* <div className="absolute top-3 right-3 bg-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-semibold text-gray-900">{property.rating}</span>
                </div> */}
                <div className="absolute top-3 left-3 bg-secondary text-white px-3 py-1 rounded-full text-xs font-medium">
                    {property?.type_details?.name || property?.property_type?.name || 'Property'}
                </div>
            </div>

            <div className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-1 line-clamp-1">{property.title}</h3>
                <p className="text-sm text-gray-500 mb-3 flex items-center gap-1">
                    <span>{property?.location?.address || property?.area_details?.name || property?.community?.name}</span>
                </p>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                    {property.description}
                </p>

                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1.5">
                        <Bed className="w-4 h-4 text-gray-400" />
                        <span>{property.bedrooms} bed{Number(property.bedrooms) > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Bath className="w-4 h-4 text-gray-400" />
                        <span>{property.bathrooms} bath{Number(property.bathrooms) > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>{property.max_guests} guests</span>
                    </div>
                </div>

                {/* <div className="mb-4">
                    {(() => {
                        // Check if amenities exist and are in the new grouped format
                        if (!property.amenities || !Array.isArray(property.amenities)) {
                            return null;
                        }

                        // Show only first 2 amenity groups, max 3 amenities per group
                        const visibleGroups = property.amenities.slice(0, 2);
                        const remainingCount = property.amenities.slice(2).reduce((total, group) => total + (group.value?.length || 0), 0);

                        return (
                            <div className="space-y-2">
                                {visibleGroups.map((group, groupIndex) => (
                                        <div key={groupIndex}>
                                        <div className="text-xs font-medium text-gray-500 mb-1">{group.title}</div>
                                        <div className="flex flex-wrap gap-1">
                                            {(group.value || []).slice(0, 3).map((amenity, index) => (
                                                <span
                                                    key={`${groupIndex}-${index}`}
                                                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                                                >
                                                    {amenity.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                </span>
                                            ))}
                                            {(group.value || []).length > 3 && (
                                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                                    +{(group.value || []).length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {remainingCount > 0 && (
                                    <div className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full inline-block">
                                        +{remainingCount} more amenities
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div> */}

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                        <span className="text-2xl font-bold text-gray-900">AED {property.price || property?.basic_price || 0}</span>
                        <span className="text-sm text-gray-500"> / Night</span>
                    </div>
                    <Link href={`/details/${property.id}`}>
                        <Button>
                            View Details
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
